import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Menu, Dropdown, Icon, Label, Button, Tab, Popup, Modal, Input } from 'semantic-ui-react';
import { useAuth } from '../../context/AuthContext';
import { useQuiz } from '../../context/QuizContext';
import { contractService, PendingSellerContractDto, EContractStatus } from '../../services/contracts/ContractService';
import { useToast } from '../../hooks/useToast';
import { useNavigate, Link } from 'react-router-dom';
import { formatCnpj } from '../../services/utils/stringUtils';
import { API_CONFIG } from '../../config/api.config';

const HEADER_POLL_BASE_MS = 10 * 60 * 1000;
const HEADER_POLL_JITTER_MS = 120 * 1000;
const getHeaderPollDelayMs = () =>
  HEADER_POLL_BASE_MS + Math.floor(Math.random() * (HEADER_POLL_JITTER_MS + 1));
const getHeaderInitialDelayMs = () => Math.floor(Math.random() * (HEADER_POLL_JITTER_MS + 1));

const Header: React.FC = () => {
  const { user, logout, hasRole } = useAuth();
  const { isQuizInProgress } = useQuiz();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [pendingContracts, setPendingContracts] = useState<PendingSellerContractDto[]>([]);
  const [myContracts, setMyContracts] = useState<PendingSellerContractDto[]>([]);
  const [teamContracts, setTeamContracts] = useState<PendingSellerContractDto[]>([]);
  /** Contadores leves do poll (badge); listas só ao abrir o sino. */
  const [pendingSellerCount, setPendingSellerCount] = useState(0);
  const [pendingTeamCount, setPendingTeamCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [suspensionReminders, setSuspensionReminders] = useState<any[]>([]);
  const [suspensionCount, setSuspensionCount] = useState(0);
  const [trocaLinhaCount, setTrocaLinhaCount] = useState(0);
  const [trocaLinhaLines, setTrocaLinhaLines] = useState<any[]>([]);
  const [loadingSuspensions, setLoadingSuspensions] = useState(false);
  const [loadingTrocaLinha, setLoadingTrocaLinha] = useState(false);
  const [activeNotificationTab, setActiveNotificationTab] = useState(0);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const headerPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pendingBadgeCount = pendingSellerCount + pendingTeamCount;
  const notificationBadgeTotal = pendingBadgeCount + suspensionCount + trocaLinhaCount;

  const handleLogout = () => {
    logout();
  };

  const loadPendingContracts = useCallback(async () => {
    // Listas completas — só ao abrir o sino (ou após Assinado/Não Assinado)
    if (!hasRole('Seller') && !hasRole('Admin') && !hasRole('TeamLeader')) return;
    
    try {
      setLoadingNotifications(true);
      
      if (hasRole('TeamLeader')) {
        const [myContractsData, teamContractsData] = await Promise.all([
          contractService.getPendingSellerContracts(),
          contractService.getPendingTeamContracts()
        ]);
        setMyContracts(myContractsData);
        setTeamContracts(teamContractsData);
        setPendingContracts([...myContractsData, ...teamContractsData]);
        setPendingSellerCount(myContractsData.length);
        setPendingTeamCount(teamContractsData.length);
      } else {
        const contracts = await contractService.getPendingSellerContracts();
        setMyContracts(contracts);
        setTeamContracts([]);
        setPendingContracts(contracts);
        setPendingSellerCount(contracts.length);
        setPendingTeamCount(0);
      }
    } catch (error) {
      console.error('Erro ao carregar contratos pendentes:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, [hasRole]);

  const loadPendingCounts = useCallback(async () => {
    if (!hasRole('Seller') && !hasRole('Admin') && !hasRole('TeamLeader')) return;

    try {
      if (hasRole('TeamLeader')) {
        const [sellerCount, teamCount] = await Promise.all([
          contractService.getPendingSellerCount(),
          contractService.getPendingTeamCount(),
        ]);
        setPendingSellerCount(sellerCount || 0);
        setPendingTeamCount(teamCount || 0);
      } else {
        const sellerCount = await contractService.getPendingSellerCount();
        setPendingSellerCount(sellerCount || 0);
        setPendingTeamCount(0);
      }
    } catch (error) {
      console.error('Erro ao carregar contadores de contratos pendentes:', error);
    }
  }, [hasRole]);

  const loadSuspensionReminders = useCallback(async () => {
    if (!hasRole('Admin') && !hasRole('TeamManager') && !hasRole('Seller') && !hasRole('TeamLeader')) return;
    
    try {
      setLoadingSuspensions(true);
      const response = await fetch('/api/v1/notifications/suspension-reminders', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuspensionReminders(data);
      }
    } catch (error) {
      console.error('Erro ao carregar lembretes de suspensão:', error);
    } finally {
      setLoadingSuspensions(false);
    }
  }, [hasRole]);

  const loadSuspensionCount = useCallback(async (signal?: AbortSignal) => {
    if (!hasRole('Admin') && !hasRole('TeamManager') && !hasRole('Seller') && !hasRole('TeamLeader')) return;
    
    try {
      const response = await fetch('/api/v1/notifications/suspension-count', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        signal,
      });

      if (signal?.aborted) return;
      if (response.ok) {
        const data = await response.json();
        setSuspensionCount(data.count || 0);
      }
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Erro ao carregar contador de suspensões:', error);
    }
  }, [hasRole]);

  const loadTrocaLinhaCount = useCallback(async () => {
    if (!hasRole('Admin') && !hasRole('TeamManager') && !hasRole('Seller') && !hasRole('TeamLeader')) return;
    
    try {
      const count = await contractService.getTrocaLinhaCount();
      setTrocaLinhaCount(count || 0);
    } catch (error) {
      console.error('Erro ao carregar contador de Troca de Linha:', error);
    }
  }, [hasRole]);

  const loadTrocaLinhaLines = useCallback(async () => {
    if (!hasRole('Admin') && !hasRole('TeamManager') && !hasRole('Seller') && !hasRole('TeamLeader')) return;
    
    try {
      setLoadingTrocaLinha(true);
      const lines = await contractService.getTrocaLinhaLines();
      setTrocaLinhaLines(lines);
    } catch (error) {
      console.error('Erro ao carregar linhas Troca de Linha:', error);
    } finally {
      setLoadingTrocaLinha(false);
    }
  }, [hasRole]);

  /** Poll periódico: só counts (anti thundering herd com jitter). */
  const loadHeaderCounts = useCallback(async (signal?: AbortSignal) => {
    await Promise.all([
      loadPendingCounts(),
      loadSuspensionCount(signal),
      loadTrocaLinhaCount(),
    ]);
  }, [loadPendingCounts, loadSuspensionCount, loadTrocaLinhaCount]);

  /** Listas pesadas — ao abrir o dropdown/sino. */
  const loadHeaderListsOnOpen = useCallback(() => {
    void loadPendingContracts();
    void loadSuspensionReminders();
    void loadTrocaLinhaLines();
  }, [loadPendingContracts, loadSuspensionReminders, loadTrocaLinhaLines]);

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    const clearPollTimeout = () => {
      if (headerPollTimeoutRef.current) {
        clearTimeout(headerPollTimeoutRef.current);
        headerPollTimeoutRef.current = null;
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      clearPollTimeout();
      headerPollTimeoutRef.current = setTimeout(async () => {
        if (cancelled || abortController.signal.aborted) return;
        await loadHeaderCounts(abortController.signal);
        scheduleNext();
      }, getHeaderPollDelayMs());
    };

    // Delay inicial aleatório (0..120s) espalha o 1º poll entre sessões
    clearPollTimeout();
    headerPollTimeoutRef.current = setTimeout(async () => {
      if (cancelled || abortController.signal.aborted) return;
      await loadHeaderCounts(abortController.signal);
      scheduleNext();
    }, getHeaderInitialDelayMs());

    return () => {
      cancelled = true;
      abortController.abort();
      clearPollTimeout();
    };
  }, [loadHeaderCounts]);

  const handleUpdateStatus = async (contractId: string, status: EContractStatus, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setUpdatingStatus(contractId);
      await contractService.updateContractStatus(contractId, status);
      showSuccess(`Status do contrato atualizado para "${status === EContractStatus.Assinado ? 'Assinado' : 'Não Assinado'}"!`);
      // Recarregar lista após atualização
      await loadPendingContracts();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showError('Erro ao atualizar status do contrato');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleViewContract = (contractId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate('/contracts', { state: { highlightContractId: contractId } });
  };

  const handleChangePassword = async () => {
    // Validações
    if (!currentPassword) {
      showError('A senha atual é obrigatória');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      showError('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('A confirmação da senha não confere');
      return;
    }

    try {
      setChangingPassword(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_CONFIG.baseURL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showSuccess('Senha alterada com sucesso!');
        setChangePasswordModalOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showError(data.message || 'Erro ao alterar senha');
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      showError('Erro ao alterar senha. Tente novamente.');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Menu
      inverted
      style={{
        margin: 0,
        borderRadius: 0,
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 1001,
      }}
    >
      <Menu.Item>
        <Icon name="building" />
        <strong>Gettel</strong> - Sistema de Gestão
      </Menu.Item>

      <Menu.Menu position="right">
        {/* Botão de Aprendizado - Acessível para todos os usuários autenticados */}
        <Popup
          content="Você tem um questionário em andamento. Finalize ou cancele antes de navegar."
          disabled={!isQuizInProgress}
          trigger={
            <Menu.Item 
              as={isQuizInProgress ? 'div' : Link} 
              to={isQuizInProgress ? undefined : "/learning"} 
              title={isQuizInProgress ? "Você tem um questionário em andamento" : "Centro de Aprendizado - Documentação e Questionários"}
              style={{
                cursor: isQuizInProgress ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: isQuizInProgress ? 0.5 : 1
              }}
              onMouseEnter={(e: any) => {
                if (!isQuizInProgress) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }
              }}
              onMouseLeave={(e: any) => {
                if (!isQuizInProgress) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
              onClick={(e: any) => {
                if (isQuizInProgress) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <Icon name="book" style={{ marginRight: '5px' }} />
              Aprendizado
            </Menu.Item>
          }
        />

        {/* Dropdown de notificações - Para Sellers, Admin, TeamManager e TeamLeader */}
        {(hasRole('Seller') || hasRole('Admin') || hasRole('TeamManager') || hasRole('TeamLeader')) && (
          <Dropdown
            item
            onOpen={loadHeaderListsOnOpen}
            trigger={
              <span style={{ position: 'relative' }}>
                <Icon name="bell" loading={loadingNotifications || loadingSuspensions || loadingTrocaLinha} />
                {notificationBadgeTotal > 0 && (
                  <Label 
                    circular 
                    color="red" 
                    size="mini" 
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px'
                    }}
                  >
                    {notificationBadgeTotal > 99 ? '99+' : notificationBadgeTotal}
                  </Label>
                )}
              </span>
            }
            pointing="top right"
            icon={null}
          >
            <Dropdown.Menu style={{ maxWidth: '500px', maxHeight: '600px', overflow: 'hidden', padding: 0 }}>
              <Tab
                panes={hasRole('TeamLeader') ? [
                  {
                    menuItem: {
                      key: 'my-contracts',
                      content: (
                        <span>
                          <Icon name="user" />
                          Minhas ({myContracts.length})
                        </span>
                      ),
                    },
                    render: () => (
                      <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px' }}>
                        {loadingNotifications ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="spinner" loading size="big" />
                            <p>Carregando...</p>
                          </div>
                        ) : myContracts.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="inbox" size="big" />
                            <p>Sem contratos próprios no momento</p>
                          </div>
                        ) : (
                          <>
                            {myContracts.map((contract) => {
                              const rawClientName = contract.clientName;
                              const clientName = (rawClientName && typeof rawClientName === 'string') 
                                ? rawClientName.trim() 
                                : '';
                              const cnpjFormatted = contract.clientCnpj ? formatCnpj(contract.clientCnpj) : '';
                              const displayName = clientName || cnpjFormatted || 'Cliente sem nome';
                              
                              return (
                                <React.Fragment key={contract.id}>
                                  <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                                    <div style={{ 
                                      marginBottom: '8px', 
                                      fontSize: '1em', 
                                      fontWeight: 'bold',
                                      color: '#000',
                                      minHeight: '20px',
                                      display: 'block'
                                    }}>
                                      {displayName || 'Cliente sem nome'}
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      CNPJ: {formatCnpj(contract.clientCnpj)}
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Operador: <strong>{contract.userName}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Tipo: <strong>{contract.tipoContratacao || 'N/A'}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Valor: R$ {(contract.valorTotalMensal || 0).toFixed(2).replace('.', ',')}
                                    </div>
                                    <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                                      <Button
                                        size="mini"
                                        icon="check"
                                        content="Assinado"
                                        loading={updatingStatus === contract.id}
                                        disabled={updatingStatus !== null && updatingStatus !== contract.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUpdateStatus(contract.id, EContractStatus.Assinado, e);
                                        }}
                                        style={{ flex: 1, backgroundColor: '#f2711c', color: 'white' }}
                                      />
                                      <Button
                                        size="mini"
                                        icon="cancel"
                                        content="Não Assinado"
                                        loading={updatingStatus === contract.id}
                                        disabled={updatingStatus !== null && updatingStatus !== contract.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUpdateStatus(contract.id, EContractStatus.NaoAssinado, e);
                                        }}
                                        style={{ flex: 1, backgroundColor: '#f2711c', color: 'white' }}
                                      />
                                    </div>
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </>
                        )}
                      </div>
                    ),
                  },
                  {
                    menuItem: {
                      key: 'team-contracts',
                      content: (
                        <span>
                          <Icon name="users" />
                          Equipe ({teamContracts.length})
                        </span>
                      ),
                    },
                    render: () => (
                      <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px' }}>
                        {loadingNotifications ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="spinner" loading size="big" />
                            <p>Carregando...</p>
                          </div>
                        ) : teamContracts.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="inbox" size="big" />
                            <p>Sem contratos da equipe no momento</p>
                          </div>
                        ) : (
                          <>
                            {teamContracts.map((contract) => {
                              const rawClientName = contract.clientName;
                              const clientName = (rawClientName && typeof rawClientName === 'string') 
                                ? rawClientName.trim() 
                                : '';
                              const cnpjFormatted = contract.clientCnpj ? formatCnpj(contract.clientCnpj) : '';
                              const displayName = clientName || cnpjFormatted || 'Cliente sem nome';
                              
                              return (
                                <React.Fragment key={contract.id}>
                                  <div style={{ 
                                    padding: '10px', 
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: '#f8f9fa' // Diferenciação visual para contratos da equipe
                                  }}>
                                    <div style={{ 
                                      marginBottom: '8px', 
                                      fontSize: '1em', 
                                      fontWeight: 'bold',
                                      color: '#000',
                                      minHeight: '20px',
                                      display: 'block'
                                    }}>
                                      {displayName || 'Cliente sem nome'}
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      CNPJ: {formatCnpj(contract.clientCnpj)}
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#007bff', marginBottom: '8px', fontWeight: 'bold' }}>
                                      Operador: {contract.userName}
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Tipo: <strong>{contract.tipoContratacao || 'N/A'}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Valor: R$ {(contract.valorTotalMensal || 0).toFixed(2).replace('.', ',')}
                                    </div>
                                    <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                                      <Button
                                        size="mini"
                                        icon="check"
                                        content="Assinado"
                                        loading={updatingStatus === contract.id}
                                        disabled={updatingStatus !== null && updatingStatus !== contract.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUpdateStatus(contract.id, EContractStatus.Assinado, e);
                                        }}
                                        style={{ flex: 1, backgroundColor: '#f2711c', color: 'white' }}
                                      />
                                      <Button
                                        size="mini"
                                        icon="cancel"
                                        content="Não Assinado"
                                        loading={updatingStatus === contract.id}
                                        disabled={updatingStatus !== null && updatingStatus !== contract.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUpdateStatus(contract.id, EContractStatus.NaoAssinado, e);
                                        }}
                                        style={{ flex: 1, backgroundColor: '#f2711c', color: 'white' }}
                                      />
                                    </div>
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </>
                        )}
                      </div>
                    ),
                  },
                ] : [
                  {
                    menuItem: {
                      key: 'contracts',
                      content: (
                        <span>
                          <Icon name="file text" />
                          Contratos ({pendingContracts.length})
                        </span>
                      ),
                    },
                    render: () => (
                      <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px' }}>
                        {loadingNotifications ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="spinner" loading size="big" />
                            <p>Carregando...</p>
                          </div>
                        ) : pendingContracts.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="inbox" size="big" />
                            <p>Sem notificações no momento</p>
                          </div>
                        ) : (
                          <>
                            {pendingContracts.map((contract) => {
                              // Debug: verificar o que temos
                              const rawClientName = contract.clientName;
                              const clientName = (rawClientName && typeof rawClientName === 'string') 
                                ? rawClientName.trim() 
                                : '';
                              const cnpjFormatted = contract.clientCnpj ? formatCnpj(contract.clientCnpj) : '';
                              const displayName = clientName || cnpjFormatted || 'Cliente sem nome';
                              
                              return (
                                <React.Fragment key={contract.id}>
                                  <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                                    {/* Nome do Cliente - sempre exibir */}
                                    <div style={{ 
                                      marginBottom: '8px', 
                                      fontSize: '1em', 
                                      fontWeight: 'bold',
                                      color: '#000',
                                      minHeight: '20px',
                                      display: 'block'
                                    }}>
                                      {displayName || 'Cliente sem nome'}
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      CNPJ: {formatCnpj(contract.clientCnpj)}
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Operador: <strong>{contract.userName}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Tipo: <strong>{contract.tipoContratacao || 'N/A'}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                      Valor: R$ {(contract.valorTotalMensal || 0).toFixed(2).replace('.', ',')}
                                    </div>
                                  <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                                    <Button
                                      size="mini"
                                      positive
                                      icon="check"
                                      content="Assinado"
                                      loading={updatingStatus === contract.id}
                                      disabled={updatingStatus !== null && updatingStatus !== contract.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUpdateStatus(contract.id, EContractStatus.Assinado, e);
                                      }}
                                      style={{ flex: 1 }}
                                    />
                                    <Button
                                      size="mini"
                                      icon="cancel"
                                      content="Não Assinado"
                                      loading={updatingStatus === contract.id}
                                      disabled={updatingStatus !== null && updatingStatus !== contract.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUpdateStatus(contract.id, EContractStatus.NaoAssinado, e);
                                      }}
                                      style={{ flex: 1, backgroundColor: '#f2711c', color: 'white' }}
                                    />
                                  </div>
                                </div>
                              </React.Fragment>
                              );
                            })}
                          </>
                        )}
                      </div>
                    ),
                  },
                  {
                    menuItem: {
                      key: 'suspensions',
                      content: (
                        <span>
                          <Icon name="pause circle" />
                          Suspensões ({suspensionCount})
                        </span>
                      ),
                    },
                    render: () => (
                      <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px' }}>
                        {loadingSuspensions ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="spinner" loading size="big" />
                            <p>Carregando...</p>
                          </div>
                        ) : suspensionReminders.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="inbox" size="big" />
                            <p>Nenhuma suspensão pendente</p>
                          </div>
                        ) : (
                          <>
                            {suspensionReminders.slice(0, 10).map((line) => {
                              const getUrgencyColor = (dias: number | null) => {
                                if (!dias) return 'grey';
                                if (dias <= 3) return 'red';
                                if (dias <= 7) return 'orange';
                                return 'yellow';
                              };
                              
                              return (
                                <div key={line.id} style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                    <strong>{line.numeroLinha || `${line.ddd} - Aditivo`}</strong>
                                    <Label 
                                      size="mini" 
                                      color={getUrgencyColor(line.diasRestantes) as any}
                                    >
                                      {line.diasRestantes !== null 
                                        ? `${line.diasRestantes} ${line.diasRestantes === 1 ? 'dia' : 'dias'}`
                                        : 'Sem data'}
                                    </Label>
                                  </div>
                                  <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '5px' }}>
                                    <strong>{line.clientName}</strong>
                                  </div>
                                  <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '5px' }}>
                                    Contrato: {line.contractNumber}
                                  </div>
                                  <div style={{ fontSize: '0.85em', color: '#666' }}>
                                    {line.temCredenciais ? (
                                      <Label color="green" size="tiny">Credenciais disponíveis</Label>
                                    ) : (
                                      <Label color="red" size="tiny">Faltando credenciais</Label>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {suspensionReminders.length > 10 && (
                              <div style={{ textAlign: 'center', padding: '10px', borderTop: '1px solid #eee', marginTop: '10px' }}>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    navigate('/suspensions');
                                  }}
                                >
                                  Ver todas ({suspensionReminders.length})
                                </Button>
                              </div>
                            )}
                            {suspensionReminders.length <= 10 && (
                              <div style={{ textAlign: 'center', padding: '10px', borderTop: '1px solid #eee', marginTop: '10px' }}>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    navigate('/suspensions');
                                  }}
                                >
                                  Ver todas
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ),
                  },
                  {
                    menuItem: {
                      key: 'troca-linha',
                      content: (
                        <span>
                          <Icon name="refresh" />
                          Troca de Linha ({trocaLinhaCount})
                        </span>
                      ),
                    },
                    render: () => (
                      <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px' }}>
                        {loadingTrocaLinha ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="spinner" loading size="big" />
                            <p>Carregando...</p>
                          </div>
                        ) : trocaLinhaLines.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="inbox" size="big" />
                            <p>Nenhuma linha Troca de Linha pendente</p>
                          </div>
                        ) : (
                          <>
                            {trocaLinhaLines.slice(0, 10).map((line) => (
                              <div key={line.lineId} style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                  <strong>{line.numero || `${line.ddd} - Aditivo`}</strong>
                                  <Label color="orange" size="mini">
                                    Troca de Linha
                                  </Label>
                                </div>
                                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '5px' }}>
                                  <strong>{line.clientName}</strong>
                                </div>
                                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '5px' }}>
                                  Contrato: {line.contractNumber}
                                </div>
                                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '5px' }}>
                                  Criado por: {line.userName}
                                </div>
                                <div style={{ marginTop: '8px' }}>
                                  <Button
                                    size="mini"
                                    icon="eye"
                                    content="Ver Contrato"
                                    onClick={() => {
                                      navigate('/contracts', { state: { highlightContractId: line.contractId } });
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                            {trocaLinhaLines.length > 10 && (
                              <div style={{ textAlign: 'center', padding: '10px', borderTop: '1px solid #eee', marginTop: '10px' }}>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    // Abrir NotificationCenter na tab de Troca de Linha
                                    // Por enquanto, apenas navegar para contracts
                                    navigate('/contracts');
                                  }}
                                >
                                  Ver todas ({trocaLinhaLines.length})
                                </Button>
                              </div>
                            )}
                            {trocaLinhaLines.length <= 10 && trocaLinhaLines.length > 0 && (
                              <div style={{ textAlign: 'center', padding: '10px', borderTop: '1px solid #eee', marginTop: '10px' }}>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    navigate('/contracts');
                                  }}
                                >
                                  Ver todas
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </Dropdown.Menu>
          </Dropdown>
        )}

        <Menu.Item>
          <Icon name="user" />
          {user?.name}
        </Menu.Item>

        <Dropdown item text="Menu">
          <Dropdown.Menu>
            <Dropdown.Item>
              <Icon name="user" />
              Perfil
            </Dropdown.Item>
            <Dropdown.Item>
              <Icon name="setting" />
              Configurações
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setChangePasswordModalOpen(true)}>
              <Icon name="lock" />
              Alterar Senha
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleLogout}>
              <Icon name="sign out" />
              Sair
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <Modal
          open={changePasswordModalOpen}
          onClose={() => {
            setChangePasswordModalOpen(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
          }}
          size="small"
        >
          <Modal.Header>Alterar Senha</Modal.Header>
          <Modal.Content>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <Input
                type="password"
                placeholder="Senha atual"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                icon="lock"
                iconPosition="left"
                fluid
              />
              <Input
                type="password"
                placeholder="Nova senha (mínimo 6 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                icon="lock"
                iconPosition="left"
                fluid
              />
              <Input
                type="password"
                placeholder="Confirmar nova senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                icon="lock"
                iconPosition="left"
                fluid
              />
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={() => {
                setChangePasswordModalOpen(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              Cancelar
            </Button>
            <Button
              primary
              onClick={handleChangePassword}
              loading={changingPassword}
              disabled={changingPassword}
            >
              Alterar Senha
            </Button>
          </Modal.Actions>
        </Modal>

        {/* Badge com roles do usuário */}
        {user?.roles && user.roles.length > 0 && (
          <Menu.Item>
            {user.roles.map((role, index) => (
              <Label key={index} color="blue" size="tiny" style={{ marginLeft: '5px' }}>
                {role}
              </Label>
            ))}
          </Menu.Item>
        )}
      </Menu.Menu>
    </Menu>
  );
};

export default Header;
